import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  DataTable,
  Modal,
  FormLayout,
  TextField,
  Banner,
  Text,
  Badge,
  ButtonGroup,
  Icon,
  Tooltip,
  Loading,
  Stack,
  Box,
  Divider
} from '@shopify/polaris';
import { EditMinor, DeleteMinor, RefreshMinor, PlusMinor } from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import {
  getNamePoolsByUserId,
  createNamePool,
  updateNamePool,
  deleteNamePool
} from '../services/namePoolService';

const NamePools = () => {
  const { user } = useAuth();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPool, setCurrentPool] = useState({ id: null, name: '', usedNames: [] });
  const [newNamesInput, setNewNamesInput] = useState('');

  const loadPools = useCallback(async () => {
    if (!user?.uid) return;
    try {
      setLoading(true);
      setError('');
      const poolsData = await getNamePoolsByUserId(user.uid);
      setPools(poolsData);
    } catch (err) {
      console.error('Failed to load name pools:', err);
      setError('Failed to load name pools. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPools();
  }, [loadPools]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setCurrentPool({ id: null, name: '', usedNames: [] });
    setNewNamesInput('');
    setError('');
  };

  const handleCreateNew = () => {
    setIsEditing(false);
    setCurrentPool({ id: null, name: '', usedNames: [] });
    setNewNamesInput('');
    setIsModalOpen(true);
  };

  const handleEdit = (pool) => {
    setIsEditing(true);
    setCurrentPool({ ...pool });
    setNewNamesInput(pool.usedNames ? pool.usedNames.join(', ') : '');
    setIsModalOpen(true);
  };

  const handleDelete = async (poolId) => {
    if (window.confirm('Are you sure you want to delete this Name Pool? This cannot be undone.')) {
      try {
        setLoading(true);
        await deleteNamePool(poolId);
        setSuccess('Name pool deleted successfully.');
        await loadPools();
      } catch (err) {
        console.error('Error deleting pool:', err);
        setError('Failed to delete name pool.');
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (!currentPool.name.trim()) {
      setError('Please provide a name for the pool.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Parse comma-separated names into an array
      let rawNames = [];
      if (newNamesInput.trim()) {
        rawNames = newNamesInput.split(',').map(n => n.trim()).filter(n => n.length > 0);
      }
      // Remove duplicates
      const uniqueNames = [...new Set(rawNames)];

      const poolData = {
        name: currentPool.name.trim(),
        usedNames: uniqueNames
      };

      if (isEditing && currentPool.id) {
        await updateNamePool(currentPool.id, poolData);
        setSuccess('Name pool updated successfully.');
      } else {
        await createNamePool(user.uid, poolData);
        setSuccess('Name pool created successfully.');
      }

      handleModalClose();
      await loadPools();
    } catch (err) {
      console.error('Error saving pool:', err);
      setError('Failed to save name pool.');
      setLoading(false);
    }
  };

  // Prepare table rows
  const rows = pools.map((pool) => [
    <Text variant="bodyMd" fontWeight="bold">{pool.name}</Text>,
    <Badge tone="info">{pool.usedNames?.length || 0} Names</Badge>,
    <ButtonGroup>
      <Tooltip content="Edit Pool">
        <Button size="slim" icon={EditMinor} onClick={() => handleEdit(pool)} />
      </Tooltip>
      <Tooltip content="Delete Pool">
        <Button size="slim" tone="critical" icon={DeleteMinor} onClick={() => handleDelete(pool.id)} />
      </Tooltip>
    </ButtonGroup>
  ]);

  return (
    <Page
      title="Name Registries (Pools)"
      subtitle="Manage lists of AI-generated product names to prevent duplicates during bulk generation."
      primaryAction={{
        content: 'Create Pool',
        icon: PlusMinor,
        onAction: handleCreateNew
      }}
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshMinor,
          onAction: loadPools
        }
      ]}
    >
      <Layout>
        <Layout.Section>
          {error && (
            <Box paddingBlockEnd="4">
              <Banner tone="critical" onDismiss={() => setError('')}>
                <p>{error}</p>
              </Banner>
            </Box>
          )}

          {success && (
            <Box paddingBlockEnd="4">
              <Banner tone="success" onDismiss={() => setSuccess('')}>
                <p>{success}</p>
              </Banner>
            </Box>
          )}

          <Stack vertical spacing="loose">
            <Card>
              <Box padding="400">
                <Stack vertical spacing="loose">
                  <Text as="p" variant="bodyMd">
                    Name Pools allow you to track which names the AI has already generated for your products. 
                    If you link a pool to a Title template, the AI will invent a completely new name and automatically add it here so it's never used twice.
                  </Text>
                  
                  {loading && !isModalOpen ? (
                    <Box padding="400" textAlign="center">
                      <Loading />
                    </Box>
                  ) : (
                    <Box paddingBlockStart="200">
                      <DataTable
                        columnContentTypes={['text', 'numeric', 'text']}
                        headings={['Pool Name', 'Used Names Count', 'Actions']}
                        rows={rows}
                        footerContent={
                          pools.length === 0 ? 'No Name Pools found. Create one to get started.' : undefined
                        }
                      />
                    </Box>
                  )}
                </Stack>
              </Box>
            </Card>
          </Stack>
        </Layout.Section>
      </Layout>

      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        title={isEditing ? 'Edit Name Pool' : 'Create Name Pool'}
        primaryAction={{
          content: isEditing ? 'Save Changes' : 'Create Pool',
          onAction: handleSave,
          loading: loading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleModalClose
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {error && <Banner tone="critical"><p>{error}</p></Banner>}
            
            <TextField
              label="Pool Name"
              value={currentPool.name}
              onChange={(val) => setCurrentPool({ ...currentPool, name: val })}
              placeholder="e.g. Handbag Names"
              autoComplete="off"
            />
            
            <Stack vertical spacing="tight">
              <TextField
                label="Registered Names"
                helpText="Comma separated list of names (e.g., Aria, Stella, Luna). The AI will automatically add new names here during bulk execution."
                value={newNamesInput}
                onChange={setNewNamesInput}
                multiline={6}
                autoComplete="off"
              />
              {isEditing && currentPool.usedNames?.length > 0 && (
                <Text variant="bodySm" tone="subdued">
                  Current total: {currentPool.usedNames.length} names
                </Text>
              )}
            </Stack>
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default NamePools;
